import React from "react";
import { Helmet } from "react-helmet";
import { useStagingPreview } from "./context";

/**
 * Injects security/indexing meta tags into the document head while the browser
 * session is previewing a staging cycle. Specifically:
 * <ul>
 *   <li>{@code <meta name="robots" content="noindex,nofollow">} — blocks
 *       search-engine indexing of any URL rendered mid-preview (SSR only
 *       stamps this on {@code /staging/*}; after the entry redirect the user
 *       is on e.g. {@code /category/phones}, so we need a client-side guard
 *       on every route).
 *   <li>{@code <meta name="googlebot" content="noindex,nofollow">} — Googlebot
 *       honours the {@code googlebot} variant with higher precedence.
 *   <li>{@code <meta name="referrer" content="no-referrer">} — prevents the
 *       cycle id (which is carried in the URL while on the entry page) from
 *       leaking via the {@code Referer} header to any external resource
 *       (analytics beacon, image CDN, third-party script, outbound link).
 *       Server-side {@code Referrer-Policy} is the belt; this is the
 *       suspenders.
 * </ul>
 *
 * <p>Complementary to the server-side {@code X-Robots-Tag} response header
 * set by {@code StagingRequestInterceptor} — some crawlers only honour the
 * meta-tag variant, and a crawler that ignores the header (or never reaches
 * grm-bff because Gatsby is serving a static HTML shell) still sees the
 * meta tag.
 *
 * <p>This component MUST be mounted high in the tree (inside
 * {@code AppContent}) so it runs on every route, not only {@code /staging/}.
 * Mounting it on the entry page alone would leave the meta tags absent
 * after the post-entry redirect to {@code /category/phones} et al.
 *
 * <p>Renders nothing (no DOM output) when the session is not staging.
 */
export function StagingHeadTags() {
  const { cycleId } = useStagingPreview();
  if (cycleId === null) {
    return null;
  }
  return (
    <Helmet>
      <meta name="robots" content="noindex,nofollow" />
      <meta name="googlebot" content="noindex,nofollow" />
      <meta name="referrer" content="no-referrer" />
    </Helmet>
  );
}
